import { ReactNode, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Database } from '@/integrations/supabase/types';
import { Button } from '@/components/ui/button';

type AppRole = Database['public']['Enums']['app_role'];

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: AppRole;
}

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, userRole, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to appropriate dashboard if no specific role required
    if (!loading && user && userRole && !requiredRole) {
      const currentPath = window.location.pathname;
      const expectedPath = userRole === 'creator' ? '/creator' : '/fan';
      
      // Only redirect if we're on a generic protected route
      if (currentPath === '/fan' && userRole === 'creator') {
        navigate('/creator', { replace: true });
      } else if (currentPath === '/creator' && userRole === 'fan') {
        navigate('/fan', { replace: true });
      }
    }
  }, [user, userRole, loading, requiredRole, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    // Allow admins to access creator features
    const isAdminAccessingCreator = userRole === 'admin' && requiredRole === 'creator';
    
    if (!isAdminAccessingCreator) {
      const redirectPath = userRole === 'creator' ? '/creator' : 
                          userRole === 'admin' ? '/admin' : '/fan-dashboard';
      
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-4xl font-bold mb-4">403</h1>
            <h2 className="text-2xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-6">
              You don't have permission to access this page. This area is restricted to {requiredRole}s only.
            </p>
            <Button onClick={() => navigate(redirectPath)}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
